import {
	faCircleCheck,
	faCircleXmark,
	faMinus,
	faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ButtonAtom, DebouncedInput } from "../../../../../components/atoms/atom";
import {
	formatCurrencyToLocale,
	formatQuantityToLocale,
	formatValueToLocale,
	getFormattedDate,
} from "../../../../../utils/method";
import PositionCommentInput from "./PositionCommentInput";

const AufmassPositionTableRow = React.memo(
	({
		position,
		index,
		positionsData,
		expandedMeasurements,
		toggleMeasurement,
		isReadonly,
		t,
		reduxPositions,
		carriedForwardData,
		addedGroups,
		savingQuickEntry,
		handleQuickEntryBlur,
		handleAddGroup,
		handleValidateClick,
		handlePositionCommentSave,
	}) => {
		const positionData = positionsData[position.id] || {};
		const mergedPositionData = positionData;
		const measuredQty = mergedPositionData.aufmass?.quantity || 0;
		const invoiceQty = mergedPositionData.invoice?.quantity || 0;
		const measurementPercentage =
			invoiceQty > 0 ? (measuredQty / invoiceQty) * 100 : 0;
		const isValidated =
			positionData.isValidated !== undefined
				? positionData.isValidated
				: true;
		const invalidatedAt = positionData.invalidatedAt;
		const invalidationReason = positionData.invalidationReason;
		const isExpanded = expandedMeasurements.has(position.id);

		const backgroundColorClass = useMemo(() => {
			if (!isValidated) return "row-bg-invalidated";
			if (!isNaN(measurementPercentage) && measuredQty > 0) {
				if (measurementPercentage > 100) return "row-bg-over-100";
				if (measurementPercentage === 100) return "row-bg-exactly-100";
				if (measurementPercentage >= 85) return "row-bg-warning";
				return "row-bg-with-quantity";
			}
			return index % 2 === 0 ? "row-bg-default-even" : "row-bg-default-odd";
		}, [
			isValidated,
			measurementPercentage,
			measuredQty,
			index,
		]);

		const previousQuantity = useMemo(() => {
			const reduxPositionData = reduxPositions[position.id];
			const invoicePositionId = reduxPositionData?.invoicePositionId;
			const carriedForwardGroups =
				carriedForwardData[invoicePositionId] || [];
			return carriedForwardGroups.reduce((sum, group) => {
				return (
					sum +
					(group.lines || []).reduce(
						(lineSum, line) =>
							lineSum + parseFloat(line.quantity || 0),
						0,
					)
				);
			}, 0);
		}, [reduxPositions, position.id, carriedForwardData]);

		const hasCurrentData = (addedGroups[position.id] || []).length > 0;

		return (
			<div className={`position-row ${backgroundColorClass}`}>
					<div className="column col-sticky-1 center-column">
						<button
							type="button"
							onClick={() => toggleMeasurement(position.id)}
							className="aufmass-expand-btn"
							title={t("common.expand")}
							tabIndex={-1}
						>
							<FontAwesomeIcon
								icon={isExpanded ? faMinus : faPlus}
								className="icon"
							/>
						</button>
					</div>

					<div className="column col-sticky-2 center-column">
						{!isValidated ? (
							<div className="aufmass-invalidation-cell">
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id={`invalidated-tooltip-${position.id}`}>
											<div className="invalidated-tooltip">
												<div className="invalidated-tooltip__header">
													<FontAwesomeIcon
														icon={faCircleXmark}
														className="invalidated-tooltip__icon"
													/>
													<span className="invalidated-tooltip__title">
														{t("aufmass.invalidated")}
													</span>
												</div>
												<div className="invalidated-tooltip__reason">
													{invalidationReason
														? t(`aufmass.${invalidationReason}`)
														: t("aufmass.invalidated")}
												</div>
												{invalidatedAt && (
													<div className="invalidated-tooltip__date">
														{getFormattedDate(invalidatedAt)}
													</div>
												)}
											</div>
										</Tooltip>
									}
								>
									<span>
										<FontAwesomeIcon
											icon={faCircleXmark}
											className="aufmass-invalidation-icon"
										/>
									</span>
								</OverlayTrigger>
								{!isReadonly && (
									<ButtonAtom
										className="btn btn-success btn-sm aufmass-validate-btn aufmass-validate-btn--pulse"
										onClick={() =>
											handleValidateClick(positionData.invoicePositionId)
										}
										title={t("aufmass.validate-action-hint")}
										tabIndex={-1}
									>
										<FontAwesomeIcon icon={faCircleCheck} className="icon" />
									</ButtonAtom>
								)}
							</div>
						) : (
							<OverlayTrigger
								placement="top"
								overlay={
									<Tooltip id={`validated-tooltip-${position.id}`}>
										{t("aufmass.validated")}
									</Tooltip>
								}
							>
								<span>
									<FontAwesomeIcon
										icon={faCircleCheck}
										className="aufmass-validated-icon"
									/>
								</span>
							</OverlayTrigger>
						)}
					</div>

					<div className="column col-sticky-3 position-number-column">
						{position.itemNumber}
					</div>

					<div
						className="column col-sticky-4 short-description-column"
						title={position.description || ""}
					>
						{position.description || "-"}
					</div>

				<div className="column numeric-column">
					{formatQuantityToLocale(position.quantity)}
				</div>

				<div className="column numeric-column">
					{formatQuantityToLocale(mergedPositionData.invoice?.quantity || 0)}
				</div>

				<div className="column numeric-column font-weight-600 text-success">
					{previousQuantity > 0 ? (
						<span
							className="aufmass-measured-value ept-clickable"
							onClick={() => toggleMeasurement(position.id)}
						>
							{formatQuantityToLocale(previousQuantity)}
						</span>
					) : (
						<span>-</span>
					)}
				</div>

				<div className="column numeric-column font-weight-600 text-success">
					{isReadonly ? (
						<span>
							{measuredQty > 0 ? formatQuantityToLocale(measuredQty) : "-"}
						</span>
					) : hasCurrentData ? (
						<div className="aufmass-measured-wrapper">
							<span
								className="aufmass-measured-value ept-clickable"
								onClick={() => toggleMeasurement(position.id)}
							>
								{formatQuantityToLocale(measuredQty)}
							</span>
							<ButtonAtom
								className="btn btn-success btn-xs aufmass-add-btn"
								onClick={(e) => {
									e.stopPropagation();
									if (!isExpanded) toggleMeasurement(position.id);
									handleAddGroup(position);
								}}
								title={t("aufmass.add")}
								tabIndex={-1}
							>
								<FontAwesomeIcon icon={faPlus} className="icon" />
							</ButtonAtom>
						</div>
					) : (
						<div className="aufmass-inline-input-wrapper">
							<DebouncedInput
								key={`quick-entry-${position.id}-${hasCurrentData}`}
								value={
									previousQuantity > 0
										? formatQuantityToLocale(previousQuantity)
										: ""
								}
								onBlur={handleQuickEntryBlur}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										e.target.blur();
									}
								}}
								position={position}
								placeholder="-"
								decimalPlaces={3}
								className="ept-quick-input quantity-input"
								disabled={savingQuickEntry[position.id]}
							/>
							{savingQuickEntry[position.id] && (
								<div className="aufmass-quick-spinner" />
							)}
							<ButtonAtom
								className="btn btn-success btn-xs aufmass-add-btn"
								onClick={(e) => {
									e.stopPropagation();
									if (!isExpanded) toggleMeasurement(position.id);
									handleAddGroup(position);
								}}
								title={t("aufmass.add")}
								tabIndex={-1}
							>
								<FontAwesomeIcon icon={faPlus} className="icon" />
							</ButtonAtom>
						</div>
					)}
				</div>

				<div className="column center-column font-weight-600">
					{position.unit}
				</div>

				<div className="column numeric-column font-weight-600">
					{formatCurrencyToLocale(
						mergedPositionData.unitPrice || position.unitPrice || 0,
					)}
				</div>

				<div className="column numeric-column font-weight-600 text-success">
					{formatCurrencyToLocale(
						(position.quantity || 0) *
							(mergedPositionData.unitPrice || position.unitPrice || 0),
					)}
				</div>

				<div className="column numeric-column font-weight-600 text-success">
					{formatCurrencyToLocale(mergedPositionData.invoice?.amount || 0)}
				</div>

				<div className="column numeric-column font-weight-bold">
					{formatValueToLocale(mergedPositionData.invoice?.calculation || 0)}%
				</div>

				<div className="column numeric-column font-weight-600 text-success">
					{formatCurrencyToLocale(mergedPositionData.aufmass?.amount || 0)}
				</div>

				<div className="column numeric-column font-weight-bold text-success">
					{formatValueToLocale(mergedPositionData.aufmass?.calculation || 0)}%
				</div>

				<div className="column warning-column">
					{!isNaN(measurementPercentage) && measurementPercentage > 100 && (
						<div className="warning-text exceeds">
							{t("aufmass.quantity-status.exceeded-tooltip")}
						</div>
					)}
					{!isNaN(measurementPercentage) &&
						measurementPercentage < 100 &&
						measuredQty > 0 && (
							<div className="warning-text approaching">
								{t("aufmass.quantity-status.insufficient-tooltip")}
							</div>
						)}
				</div>

				<div className="column column-comment">
					<PositionCommentInput
						invoicePositionId={positionData.invoicePositionId}
						initialValue={positionData.comments || ""}
						isReadonly={isReadonly}
						onSave={handlePositionCommentSave}
						placeholder={t("aufmass.add-position-comment")}
					/>
				</div>
			</div>
		);
	},
);

export default AufmassPositionTableRow;
